import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';

// ─── types ────────────────────────────────────────────────────────────────────
type TimeInput = Date | string | null | undefined;

interface CommonProps {
  step?: number;
  is24Hour?: boolean;
  variant?: 'pill' | 'field' | 'bare';
  className?: string;
  triggerStyle?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
}
interface SingleProps extends CommonProps {
  mode?: 'single';
  value: TimeInput;
  onChange: (value: any) => void;
  output?: 'date' | 'string';
}
interface RangeProps extends CommonProps {
  mode: 'range';
  from: TimeInput;
  to: TimeInput;
  onChange: (from: any, to: any) => void;
  output?: 'date' | 'string';
  separator?: React.ReactNode;
}
type Props = SingleProps | RangeProps;

// ─── utils ────────────────────────────────────────────────────────────────────
const toMin = (v: TimeInput): number | null => {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getHours() * 60 + v.getMinutes();
  const m = String(v).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = +m[1], mi = +m[2];
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
};

const p2 = (n: number) => String(n).padStart(2, '0');

const fmt = (min: number, is24: boolean): string => {
  const m = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60), mi = m % 60;
  if (is24) return `${p2(h)}:${p2(mi)}`;
  const ap = h < 12 ? 'AM' : 'PM';
  let hh = h % 12; if (hh === 0) hh = 12;
  return `${hh}:${p2(mi)} ${ap}`;
};

const fmtMins = (min: number): string =>
  `${p2(Math.floor(((min % 1440) + 1440) % 1440 / 60))}:${p2(((min % 1440) + 1440) % 1440 % 60)}`;

const durStr = (fromM: number, toM: number): string => {
  let diff = toM - fromM;
  if (diff < 0) diff += 1440;
  const h = Math.floor(diff / 60), m = diff % 60;
  return `${h}${t('HShort')}${m ? ' ' + m + t('Min') : ''}`;
};

// ─── drum constants ───────────────────────────────────────────────────────────
const ITEM_H  = 38;  // px, matches .tpd-item height in style.css
const PAD     = 3;   // padding items above/below
const DRUM_H  = 230; // px, matches .tpd-drum height in style.css
// offset to perfectly center item[i] in drum viewport:
// center of item = PAD*ITEM_H + i*ITEM_H + ITEM_H/2
// center of viewport = scrollTop + DRUM_H/2
// => scrollTop = PAD*ITEM_H + i*ITEM_H + ITEM_H/2 - DRUM_H/2
const SCROLL_OFFSET = PAD * ITEM_H + Math.floor(ITEM_H / 2) - Math.floor(DRUM_H / 2); // = 18

// ─── drum helpers (imperative, no React) ─────────────────────────────────────
interface Slot { label: string; mins: number; next: boolean; }

function makeSlots(step: number, nextDay = false): Slot[] {
  const out: Slot[] = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += step)
      out.push({ label: `${p2(h)}:${p2(m)}`, mins: h * 60 + m + (nextDay ? 1440 : 0), next: nextDay });
  return out;
}

function buildDrum(el: HTMLDivElement, slots: Slot[], initMins: number) {
  el.innerHTML = '';
  const pt = document.createElement('div');
  pt.className = 'tpd-pad';
  pt.style.height = `${PAD * ITEM_H}px`;
  el.appendChild(pt);

  let initIdx = 0;
  slots.forEach((s, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tpd-item';
    btn.dataset.mins = String(s.mins);
    btn.textContent = s.label;
    if (s.next) {
      const badge = document.createElement('span');
      badge.className = 'tpd-nx';
      badge.textContent = '+1';
      btn.appendChild(badge);
    }
    el.appendChild(btn);
    if (s.mins === initMins) initIdx = i;
  });

  const pb = document.createElement('div');
  pb.className = 'tpd-pad';
  pb.style.height = `${PAD * ITEM_H}px`;
  el.appendChild(pb);

  // Use SCROLL_OFFSET so selected item is exactly at visual center of drum
  el.scrollTop = SCROLL_OFFSET + initIdx * ITEM_H;
}

function styleItems(el: HTMLDivElement): number {
  const centerIdx = Math.round((el.scrollTop - SCROLL_OFFSET) / ITEM_H);
  el.querySelectorAll<HTMLElement>('.tpd-item').forEach((item, i) => {
    item.classList.toggle('tpd-selected', i === centerIdx);
  });
  return centerIdx;
}

function scrollToIdx(
  el: HTMLDivElement,
  idx: number,
  onSettle?: (mins: number) => void,
) {
  const items = el.querySelectorAll('.tpd-item');
  const clamped = Math.max(0, Math.min(idx, items.length - 1));
  (el as any)._dragging = true;
  clearTimeout((el as any)._settleTimer);
  el.scrollTo({ top: SCROLL_OFFSET + clamped * ITEM_H, behavior: 'smooth' });
  (el as any)._settleTimer = setTimeout(() => {
    (el as any)._dragging = false;
    const settled = styleItems(el);
    const center = el.querySelectorAll<HTMLElement>('.tpd-item')[settled];
    if (center && onSettle) onSettle(Number(center.dataset.mins));
  }, 350);
}

function scrollDrumToMins(el: HTMLDivElement, targetMins: number, onSettle?: (mins: number) => void) {
  let bestIdx = 0, bestDiff = Infinity;
  el.querySelectorAll<HTMLElement>('.tpd-item').forEach((item, i) => {
    const diff = Math.abs(Number(item.dataset.mins) - targetMins);
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  });
  scrollToIdx(el, bestIdx, onSettle);
}

function addDragScroll(el: HTMLDivElement, onSettle: (mins: number) => void): () => void {
  let dragging = false;
  let startY = 0, startScroll = 0, velocity = 0, lastY = 0, lastT = 0;

  const beginDrag = (clientY: number, isTouch: boolean) => {
    dragging = true;
    (el as any)._dragging = true;
    clearTimeout((el as any)._settleTimer);
    startY = clientY; startScroll = el.scrollTop;
    lastY = clientY; lastT = Date.now(); velocity = 0;
    if (!isTouch) el.style.cursor = 'grabbing';
    el.style.scrollSnapType = 'none';
  };

  const moveDrag = (clientY: number) => {
    if (!dragging) return;
    const now = Date.now(), dt = now - lastT || 1;
    velocity = (lastY - clientY) / dt;
    lastY = clientY; lastT = now;
    el.scrollTop = startScroll + (startY - clientY);
    // styleItems не вызываем во время drag — CSS mask-image уже даёт визуальный фидбек
  };

  const endDrag = (isTouch: boolean) => {
    if (!dragging) return;
    dragging = false;
    if (!isTouch) el.style.cursor = '';
    // Ограничиваем импульс — не более 6 элементов
    const momentum = Math.sign(velocity) * Math.min(Math.abs(velocity * 80), 6 * ITEM_H);
    const maxIdx = el.querySelectorAll('.tpd-item').length - 1;
    const snapIdx = Math.max(0, Math.min(
      Math.round((el.scrollTop + momentum - SCROLL_OFFSET) / ITEM_H),
      maxIdx,
    ));
    const targetTop = SCROLL_OFFSET + snapIdx * ITEM_H;
    el.scrollTo({ top: targetTop, behavior: 'smooth' });
    clearTimeout((el as any)._settleTimer);
    (el as any)._settleTimer = setTimeout(() => {
      (el as any)._dragging = false;
      el.style.scrollSnapType = '';
      // Принудительно выравниваем позицию — smooth scroll мог не дойти точно
      el.scrollTop = targetTop;
      const idx = styleItems(el);
      const center = el.querySelectorAll<HTMLElement>('.tpd-item')[idx];
      if (center) onSettle(Number(center.dataset.mins));
    }, 380);
  };

  // mouse
  const onDown  = (e: MouseEvent) => { beginDrag(e.clientY, false); e.preventDefault(); };
  const onMove  = (e: MouseEvent) => moveDrag(e.clientY);
  const onUp    = () => endDrag(false);

  // touch
  const onTouchStart = (e: TouchEvent) => beginDrag(e.touches[0].clientY, true);
  const onTouchMove  = (e: TouchEvent) => { moveDrag(e.touches[0].clientY); e.preventDefault(); };
  const onTouchEnd   = () => endDrag(true);

  const onGlobalUp = () => setTimeout(() => {}, 300);

  el.addEventListener('mousedown', onDown, { passive: false });
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: false });
  el.addEventListener('touchend', onTouchEnd, { passive: true });
  document.addEventListener('mouseup', onGlobalUp, { passive: true });

  return () => {
    el.removeEventListener('mousedown', onDown);
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchmove', onTouchMove);
    el.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('mouseup', onGlobalUp);
  };
}

// ─── DrumPickerPopup ──────────────────────────────────────────────────────────
interface PopupProps {
  isRange: boolean;
  initFrom: number;
  initTo: number;
  step: number;
  is24Hour: boolean;
  top: number;
  left: number;
  onCommit: (from: number, to: number) => void;
  onClose: () => void;
}

const DrumPickerPopup = React.forwardRef<HTMLDivElement, PopupProps>(({
  isRange, initFrom, initTo, step, top, left, onCommit, onClose,
}, popupRef) => {
  const drumFromRef = useRef<HTMLDivElement>(null);
  const drumToRef   = useRef<HTMLDivElement>(null);
  const frameRef    = useRef<HTMLDivElement>(null);
  const stateRef    = useRef({ from: initFrom, to: initTo });
  const onCommitRef = useRef(onCommit);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => { onCommitRef.current = onCommit; onCloseRef.current = onClose; });

  const [bar, setBar] = useState({ from: initFrom, to: initTo });

  const commit = useCallback((from: number, to: number) => {
    stateRef.current = { from, to };
    setBar({ from, to });
    onCommitRef.current(from, to);
  }, []);

  const highlightFrame = useCallback((which: 'from' | 'to' | null) => {
    if (!frameRef.current) return;
    frameRef.current.style.borderColor =
      which === 'from' ? 'rgba(37,99,235,.5)' :
      which === 'to'   ? 'rgba(124,58,237,.5)' : '';
  }, []);

  const stepFrom = useCallback((dir: 1 | -1) => {
    const el = drumFromRef.current; if (!el) return;
    scrollToIdx(el, Math.round((el.scrollTop - SCROLL_OFFSET) / ITEM_H) + dir, (mins) => {
      stateRef.current.from = mins;
      if (isRange && stateRef.current.from >= stateRef.current.to) {
        const newTo = stateRef.current.from + step;
        stateRef.current.to = newTo;
        if (drumToRef.current) scrollDrumToMins(drumToRef.current, newTo);
      }
      commit(stateRef.current.from, stateRef.current.to);
    });
  }, [isRange, step, commit]);

  const stepTo = useCallback((dir: 1 | -1) => {
    const el = drumToRef.current; if (!el) return;
    scrollToIdx(el, Math.round((el.scrollTop - SCROLL_OFFSET) / ITEM_H) + dir, (mins) => {
      stateRef.current.to = mins;
      commit(stateRef.current.from, stateRef.current.to);
    });
  }, [commit]);

  useEffect(() => {
    const fromEl = drumFromRef.current;
    if (!fromEl) return;

    // ── from drum ──
    buildDrum(fromEl, makeSlots(step, false), stateRef.current.from);
    styleItems(fromEl);

    const onFromSettle = (mins: number) => {
      stateRef.current.from = mins;
      if (isRange && stateRef.current.from >= stateRef.current.to) {
        const newTo = stateRef.current.from + step;
        stateRef.current.to = newTo;
        if (drumToRef.current) scrollDrumToMins(drumToRef.current, newTo);
      }
      commit(stateRef.current.from, stateRef.current.to);
    };

    const onFromClick = (e: MouseEvent) => {
      const item = (e.target as HTMLElement).closest<HTMLElement>('.tpd-item');
      if (!item) return;
      // Клик по уже подсвеченному центральному элементу — подтвердить и закрыть
      if (item.classList.contains('tpd-selected')) {
        clearTimeout((fromEl as any)._settleTimer);
        onFromSettle(Number(item.dataset.mins));
        onCloseRef.current();
        return;
      }
      const items = Array.from(fromEl.querySelectorAll<HTMLElement>('.tpd-item'));
      const idx = items.indexOf(item);
      if (idx >= 0) { highlightFrame('from'); scrollToIdx(fromEl, idx, onFromSettle); }
    };

    let nativeFromTimer: ReturnType<typeof setTimeout>;
    const onFromScroll = () => {
      if ((fromEl as any)._dragging) return;
      highlightFrame('from');
      clearTimeout(nativeFromTimer);
      nativeFromTimer = setTimeout(() => {
        if ((fromEl as any)._dragging) return;
        const idx = styleItems(fromEl);
        const c = fromEl.querySelectorAll<HTMLElement>('.tpd-item')[idx];
        if (c) onFromSettle(Number(c.dataset.mins));
      }, 150);
    };

    fromEl.addEventListener('click', onFromClick);
    fromEl.addEventListener('scroll', onFromScroll, { passive: true });
    const cleanDragFrom = addDragScroll(fromEl, onFromSettle);

    // ── to drum ──
    const toEl = isRange ? drumToRef.current : null;
    let cleanDragTo: (() => void) | undefined;
    let cleanTo: (() => void) | undefined;

    if (toEl) {
      buildDrum(toEl, [...makeSlots(step, false), { label: '00:00', mins: 1440, next: true }], stateRef.current.to);
      styleItems(toEl);

      const onToSettle = (mins: number) => {
        stateRef.current.to = mins;
        commit(stateRef.current.from, stateRef.current.to);
      };

      const onToClick = (e: MouseEvent) => {
        const item = (e.target as HTMLElement).closest<HTMLElement>('.tpd-item');
        if (!item) return;
        // Клик по уже подсвеченному центральному элементу — подтвердить и закрыть
        if (item.classList.contains('tpd-selected')) {
          clearTimeout((toEl as any)._settleTimer);
          onToSettle(Number(item.dataset.mins));
          onCloseRef.current();
          return;
        }
        const items = Array.from(toEl.querySelectorAll<HTMLElement>('.tpd-item'));
        const idx = items.indexOf(item);
        if (idx >= 0) { highlightFrame('to'); scrollToIdx(toEl, idx, onToSettle); }
      };

      let nativeToTimer: ReturnType<typeof setTimeout>;
      const onToScroll = () => {
        if ((toEl as any)._dragging) return;
        highlightFrame('to');
        clearTimeout(nativeToTimer);
        nativeToTimer = setTimeout(() => {
          if ((toEl as any)._dragging) return;
          const idx = styleItems(toEl);
          const c = toEl.querySelectorAll<HTMLElement>('.tpd-item')[idx];
          if (c) onToSettle(Number(c.dataset.mins));
        }, 150);
      };

      toEl.addEventListener('click', onToClick);
      toEl.addEventListener('scroll', onToScroll, { passive: true });
      cleanDragTo = addDragScroll(toEl, onToSettle);
      cleanTo = () => {
        toEl.removeEventListener('click', onToClick);
        toEl.removeEventListener('scroll', onToScroll);
        clearTimeout(nativeToTimer);
      };
    }

    return () => {
      fromEl.removeEventListener('click', onFromClick);
      fromEl.removeEventListener('scroll', onFromScroll);
      clearTimeout(nativeFromTimer);
      cleanDragFrom();
      cleanDragTo?.();
      cleanTo?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextDay = bar.to >= 1440;

  return (
    <div ref={popupRef} className="tpd-popup" style={{ position: 'fixed', top, left, zIndex: 'var(--z-portal)' as any, width: isRange ? 280 : 150 }}>
      <div className="tpd-labels">
        <div className="tpd-lbl">{t('Start')}</div>
        {isRange && <div className="tpd-lbl">{t('End')}</div>}
      </div>

      <div className="tpd-drums">
        <div ref={frameRef} className="tpd-frame" />
        <div ref={drumFromRef} className={`tpd-drum${isRange ? ' tpd-left' : ''}`} />
        {isRange && <div ref={drumToRef} className="tpd-drum" />}

        <div className="tpd-arrows tpd-top">
          <button type="button" className={`tpd-arr${isRange ? ' tpd-left' : ''}`} onClick={() => stepFrom(-1)}>▲</button>
          {isRange && <button type="button" className="tpd-arr" onClick={() => stepTo(-1)}>▲</button>}
        </div>
        <div className="tpd-arrows tpd-bot">
          <button type="button" className={`tpd-arr${isRange ? ' tpd-left' : ''}`} onClick={() => stepFrom(1)}>▼</button>
          {isRange && <button type="button" className="tpd-arr" onClick={() => stepTo(1)}>▼</button>}
        </div>
      </div>

      {isRange && (
        <div className="tpd-bar">
          <div className="tpd-bar-times">
            <span className="tpd-bar-from">{fmtMins(bar.from)}</span>
            <span className="tpd-bar-sep">→</span>
            <span className="tpd-bar-to">
              {fmtMins(bar.to)}{nextDay && <span className="tpd-nx">+1</span>}
            </span>
          </div>
          <div className="tpd-bar-dur">{durStr(bar.from, bar.to)}</div>
        </div>
      )}
    </div>
  );
});

// ─── TimePicker ───────────────────────────────────────────────────────────────
const TimePicker: React.FC<Props> = (props) => {
  const {
    step = 15, is24Hour = true, variant = 'pill',
    className = '', triggerStyle, placeholder = '--:--', disabled = false,
  } = props;

  const isRange = props.mode === 'range';
  const output  = (props as any).output ?? 'date';

  const curFrom = isRange ? toMin((props as RangeProps).from) : toMin((props as SingleProps).value);
  const curTo   = isRange ? toMin((props as RangeProps).to) : null;

  const [open, setOpen]     = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });
  const wrapRef   = useRef<HTMLDivElement>(null);  // cp-wrap — click-outside detection
  const posRef    = useRef<HTMLDivElement>(null);  // first trigger — popup positioning
  const popRef    = useRef<HTMLDivElement>(null);  // actual .tpd-popup — height measurement

  const emit = useCallback((mins: number): any => {
    if (output === 'string') return fmt(mins % 1440, true);
    const norm = mins % 1440;
    const d = new Date(); d.setHours(Math.floor(norm / 60), norm % 60, 0, 0); return d;
  }, [output]);

  const place = useCallback(() => {
    const a = posRef.current ?? wrapRef.current; if (!a) return;
    const r   = a.getBoundingClientRect();
    const popH = popRef.current?.offsetHeight ?? 320;
    const popW = popRef.current?.offsetWidth  ?? 280;
    const top  = (window.innerHeight - r.bottom < popH + 10 && r.top > popH)
      ? r.top - popH - 6 : r.bottom + 6;
    let left = r.left;
    if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
    setCoords({ top, left: Math.max(8, left) });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    const onKey    = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown   = (e: MouseEvent | TouchEvent) => {
      const tgt = (e instanceof MouseEvent ? e.target : e.touches[0]?.target) as Node;
      if (popRef.current?.contains(tgt) || wrapRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown as any);
    document.addEventListener('touchstart', onDown as any);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown as any);
      document.removeEventListener('touchstart', onDown as any);
    };
  }, [open, place]);

  const onCommit = useCallback((from: number, to: number) => {
    if (isRange) (props as RangeProps).onChange(emit(from), emit(to));
    else         (props as SingleProps).onChange(emit(from));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRange, emit]);

  const label       = (m: number | null) => m == null ? placeholder : fmt(m, is24Hour);
  const variantCls  = variant === 'field' ? 'cp-field' : variant === 'bare' ? 'cp-bare' : 'cp-pill';
  const triggerCls  = `cp-trigger ${variantCls} ${open ? 'cp-open' : ''} ${disabled ? 'cp-disabled-trigger' : ''} ${className}`;
  const sep         = isRange ? ((props as RangeProps).separator ?? '→') : null;
  const openPopup   = () => { if (!disabled) setOpen(true); };

  const initFrom = curFrom ?? 0;
  // Overnight detection: if to < from (e.g. 00:15 < 23:45), treat as next-day slot
  const initTo = curTo == null
    ? Math.min(initFrom + step, 1439)
    : curTo < initFrom ? curTo + 1440 : curTo;

  const popup = open ? createPortal(
    <DrumPickerPopup
      ref={popRef}
      isRange={isRange}
      initFrom={initFrom}
      initTo={initTo}
      step={step}
      is24Hour={is24Hour}
      top={coords.top}
      left={coords.left}
      onCommit={onCommit}
      onClose={() => setOpen(false)}
    />,
    document.body,
  ) : null;

  return (
    <>
      <div ref={wrapRef} className="cp-wrap" style={triggerStyle}>
        {isRange ? (
          <>
            <div ref={posRef} className={triggerCls} onClick={openPopup} role="button" tabIndex={0}>
              <span className="cp-val">{label(curFrom)}</span>
            </div>
            <span className="cp-arrow">{sep}</span>
            <div className={triggerCls} onClick={openPopup} role="button" tabIndex={0}>
              <span className="cp-val">{label(curTo)}</span>
            </div>
          </>
        ) : (
          <div ref={posRef} className={triggerCls} onClick={openPopup} role="button" tabIndex={0}>
            <span className="cp-val">{label(curFrom)}</span>
          </div>
        )}
      </div>
      {popup}
    </>
  );
};

export default TimePicker;
